import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, Download, FileDown, FileText, Loader2, LogOut, MailOpen, Save, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { downloadFile } from "@/lib/pdf";
import { useAuth } from "@/context/AuthContext";
import { FALLBACK_CONTENT, fmtPrice } from "@/lib/content";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArticlesAdmin } from "@/components/admin/ArticlesAdmin";
import { IntegrationsAdmin } from "@/components/admin/IntegrationsAdmin";
import { ChangePasswordDialog } from "@/components/admin/ChangePasswordDialog";
import { ContractEditor } from "@/components/admin/ContractEditor";
import { DocumentsAdmin } from "@/components/admin/DocumentsAdmin";
import { FaqAdmin } from "@/components/admin/FaqAdmin";
import { GoogleReviewsAdmin } from "@/components/admin/GoogleReviewsAdmin";
import { PopupEditor } from "@/components/admin/PopupEditor";
import { Billing24Editor, PackagePromoEditor, PricingPromoEditor } from "@/components/admin/PromoEditor";
import { ComparisonEditor } from "@/components/admin/ComparisonEditor";
import { StaffAdmin } from "@/components/admin/StaffAdmin";
import { SecurityAdmin } from "@/components/admin/SecurityAdmin";
import { EmailArchiveAdmin } from "@/components/admin/EmailArchiveAdmin";
import { TextsAdmin } from "@/components/admin/TextsAdmin";
import { UploadsAdmin } from "@/components/admin/UploadsAdmin";
import { CompanyCheck } from "@/components/admin/CompanyCheck";
import { StatsAdmin } from "@/components/admin/StatsAdmin";
import { EmailTemplatesAdmin } from "@/components/admin/EmailTemplatesAdmin";
import { SmtpAdmin } from "@/components/admin/SmtpAdmin";
import { ProformaRegistryAdmin } from "@/components/admin/ProformaRegistryAdmin";
import { RegisteredAction } from "@/components/admin/RegisteredAction";
import { QuotesAdmin } from "@/components/admin/QuotesAdmin";
import { PageAdmin } from "@/components/admin/PageAdmin";
import { OrderGenerator } from "@/components/admin/OrderGenerator";
import { ProformaGenerator } from "@/components/admin/ProformaGenerator";

function ForgotPasswordLink({ email }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(email || "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState("");
  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: value });
      setSent(data.message);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d === "rate_limited" ? "Zbyt wiele prób. Spróbuj później." : "Nie udało się wysłać linku.");
    } finally {
      setSending(false);
    }
  };
  if (!open)
    return <button type="button" data-testid="admin-forgot-password-link" onClick={() => { setValue(email || ""); setOpen(true); }} className="mt-4 w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors">Nie pamiętam hasła</button>;
  return (
    <div data-testid="admin-forgot-password-box" className="mt-5 rounded-xl bg-muted/60 p-4 space-y-3">
      {sent ? (
        <p data-testid="admin-forgot-password-sent" className="text-xs text-foreground">{sent}</p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">Podaj e-mail administratora — wyślemy jednorazowy link do ustawienia nowego hasła (ważny 60 minut).</p>
          <Input data-testid="admin-forgot-password-email" type="email" placeholder="E-mail" value={value} onChange={(e) => setValue(e.target.value)} />
          <button type="button" data-testid="admin-forgot-password-submit" onClick={send} disabled={sending || !value} className="w-full border border-brand-teal text-brand-tealDark dark:text-brand-teal hover:bg-brand-teal hover:text-white disabled:opacity-60 font-semibold py-2 rounded-full text-xs transition-colors inline-flex items-center justify-center gap-2">
            {sending && <Loader2 size={13} className="animate-spin" />} Wyślij link
          </button>
        </>
      )}
    </div>
  );
}

function LoginForm() {
  const { login, completeLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("password"); // password | otp | enroll | recovery
  const [challenge, setChallenge] = useState("");
  const [enroll, setEnroll] = useState(null); // { qr_data_uri, otpauth_uri }
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [pendingUser, setPendingUser] = useState(null);

  const errToast = (err, fallback) => {
    const d = err.response?.data?.detail;
    toast.error(typeof d === "string" ? d : fallback);
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.status === "otp_required") {
        setChallenge(data.challenge_id);
        setCode("");
        setStep("otp");
      } else if (data.status === "enrollment_required") {
        setChallenge(data.challenge_id);
        const { data: en } = await api.post("/auth/2fa/enroll/start", { challenge_id: data.challenge_id });
        setEnroll(en);
        setCode("");
        setStep("enroll");
      } else {
        toast.success("Zalogowano pomyślnie.");
      }
    } catch (err) {
      errToast(err, "Logowanie nie powiodło się.");
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login/otp", { challenge_id: challenge, code: code.trim() });
      toast.success("Zalogowano pomyślnie.");
      completeLogin(data);
    } catch (err) {
      errToast(err, "Nieprawidłowy kod.");
    } finally {
      setLoading(false);
    }
  };

  const submitEnroll = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/enroll/confirm", { challenge_id: challenge, code: code.trim() });
      setRecoveryCodes(data.recovery_codes || []);
      setPendingUser(data);
      setStep("recovery");
    } catch (err) {
      errToast(err, "Nieprawidłowy kod z aplikacji.");
    } finally {
      setLoading(false);
    }
  };

  const secretFromUri = () => {
    const m = enroll?.otpauth_uri?.match(/secret=([A-Z2-7]+)/);
    return m ? m[1] : "";
  };

  const downloadRecovery = () => {
    const blob = new Blob([`Kody zapasowe 2FA — Twoje Biuro\nKonto: ${email}\n\n${recoveryCodes.join("\n")}\n\nKażdy kod działa jednorazowo. Przechowuj w bezpiecznym miejscu.`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "kody-zapasowe-2fa.txt";
    a.click();
  };

  const card = "w-full max-w-sm bg-card border border-border rounded-2xl p-8 shadow-xl";
  const primaryBtn = "mt-6 w-full bg-brand-teal hover:bg-brand-tealDark disabled:opacity-60 text-white font-semibold py-2.5 rounded-full text-sm transition-colors duration-200 inline-flex items-center justify-center gap-2";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      {step === "password" && (
        <form data-testid="admin-login-form" onSubmit={submitPassword} className={card}>
          <Logo className="h-10 text-slate-900 dark:text-white" showTagline={false} />
          <p className="text-sm text-muted-foreground mt-2">Panel administratora — zaloguj się, aby edytować treści strony.</p>
          <div className="mt-6 space-y-4">
            <Input data-testid="admin-login-email-input" type="email" required placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Input data-testid="admin-login-password-input" type="password" required placeholder="Hasło" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <button data-testid="admin-login-submit-button" type="submit" disabled={loading} className={primaryBtn}>
            {loading && <Loader2 size={15} className="animate-spin" />} Zaloguj się
          </button>
          <ForgotPasswordLink email={email} />
          <Link to="/" data-testid="admin-back-home-link" className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={13} /> Wróć na stronę główną
          </Link>
        </form>
      )}

      {step === "otp" && (
        <form data-testid="admin-2fa-otp-form" onSubmit={submitOtp} className={card}>
          <Logo className="h-10 text-slate-900 dark:text-white" showTagline={false} />
          <h2 className="font-display font-bold text-lg mt-4">Weryfikacja dwuetapowa</h2>
          <p className="text-sm text-muted-foreground mt-1">Wpisz 6-cyfrowy kod z aplikacji Google Authenticator. Możesz też użyć kodu zapasowego.</p>
          <Input data-testid="admin-2fa-otp-input" autoFocus inputMode="text" autoComplete="one-time-code" placeholder="Kod (6 cyfr lub kod zapasowy)" value={code} onChange={(e) => setCode(e.target.value)} className="mt-6 text-center tracking-widest" />
          <button data-testid="admin-2fa-otp-submit" type="submit" disabled={loading || !code.trim()} className={primaryBtn}>
            {loading && <Loader2 size={15} className="animate-spin" />} Zaloguj się
          </button>
          <button type="button" data-testid="admin-2fa-back" onClick={() => { setStep("password"); setCode(""); }} className="mt-4 w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors">Wróć</button>
        </form>
      )}

      {step === "enroll" && (
        <form data-testid="admin-2fa-enroll-form" onSubmit={submitEnroll} className={card}>
          <h2 className="font-display font-bold text-lg">Skonfiguruj logowanie dwuetapowe</h2>
          <p className="text-sm text-muted-foreground mt-1">Zabezpieczenie panelu jest wymagane. Zeskanuj kod QR w aplikacji <strong>Google Authenticator</strong> (lub innej TOTP), a następnie wpisz wygenerowany kod.</p>
          {enroll?.qr_data_uri && (
            <div className="mt-4 flex justify-center">
              <img data-testid="admin-2fa-qr" src={enroll.qr_data_uri} alt="Kod QR do konfiguracji 2FA" className="w-44 h-44 rounded-lg border border-border bg-white p-2" />
            </div>
          )}
          <p className="mt-3 text-xs text-muted-foreground text-center">Nie możesz zeskanować? Wpisz klucz ręcznie:</p>
          <code data-testid="admin-2fa-secret" className="mt-1 block text-center text-xs font-mono bg-muted rounded-md py-1.5 px-2 break-all">{secretFromUri()}</code>
          <Input data-testid="admin-2fa-enroll-input" autoComplete="one-time-code" inputMode="numeric" maxLength={6} placeholder="6-cyfrowy kod z aplikacji" value={code} onChange={(e) => setCode(e.target.value)} className="mt-4 text-center tracking-widest" />
          <button data-testid="admin-2fa-enroll-submit" type="submit" disabled={loading || code.trim().length !== 6} className={primaryBtn}>
            {loading && <Loader2 size={15} className="animate-spin" />} Włącz 2FA i zaloguj
          </button>
        </form>
      )}

      {step === "recovery" && (
        <div data-testid="admin-2fa-recovery" className={card}>
          <h2 className="font-display font-bold text-lg">Zapisz kody zapasowe</h2>
          <p className="text-sm text-muted-foreground mt-1">To Twoja jedyna szansa, by je zobaczyć. Użyjesz ich, gdy stracisz dostęp do telefonu. Każdy kod działa <strong>jednorazowo</strong>.</p>
          <div className="mt-4 grid grid-cols-2 gap-2 bg-muted rounded-lg p-3">
            {recoveryCodes.map((c) => (
              <code key={c} className="text-xs font-mono text-center py-1">{c}</code>
            ))}
          </div>
          <button type="button" data-testid="admin-2fa-recovery-download" onClick={downloadRecovery} className="mt-4 w-full border border-brand-teal text-brand-tealDark dark:text-brand-teal hover:bg-brand-teal hover:text-white font-semibold py-2 rounded-full text-xs transition-colors">Pobierz kody (.txt)</button>
          <button type="button" data-testid="admin-2fa-recovery-done" onClick={() => { toast.success("2FA włączone. Zalogowano."); completeLogin(pendingUser); }} className={primaryBtn}>
            Zapisałem kody — przejdź do panelu
          </button>
        </div>
      )}
    </div>
  );
}

function ContentEditor() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/content")
      .then((r) => setContent({ ...FALLBACK_CONTENT, ...r.data }))
      .catch(() => setContent(FALLBACK_CONTENT));
  }, []);

  if (!content)
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-slate-600" />
      </div>
    );

  const setField = (k) => (e) => setContent({ ...content, [k]: e.target.value });
  const setTestimonial = (i, k, v) => {
    const testimonials = content.testimonials.map((r, idx) => (idx === i ? { ...r, [k]: v } : r));
    setContent({ ...content, testimonials });
  };
  const setPkg = (i, k, v) => {
    const packages = content.packages.map((p, idx) => (idx === i ? { ...p, [k]: v } : p));
    setContent({ ...content, packages });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", content);
      toast.success("Treści strony zapisane. Odwieź stronę główną, aby je zobaczyć.");
    } catch {
      toast.error("Nie udało się zapisać zmian.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="content-editor" className="space-y-8">
      <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
        <h3 className="font-display font-bold text-lg">Sekcja główna (hero)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Nagłówek — linia 1</label>
            <Input data-testid="edit-hero-title-1" value={content.hero_title_1} onChange={setField("hero_title_1")} />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Nagłówek — linia 2</label>
            <Input data-testid="edit-hero-title-2" value={content.hero_title_2} onChange={setField("hero_title_2")} />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Podtytuł</label>
          <Textarea data-testid="edit-hero-subtitle" rows={3} value={content.hero_subtitle} onChange={setField("hero_subtitle")} />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Pasek ogłoszenia (puste = ukryty)</label>
          <Input data-testid="edit-announcement" value={content.announcement} onChange={setField("announcement")} placeholder="np. Promocja: -20% na start" />
        </div>
      </div>

      <div data-testid="visibility-editor" className="bg-card border border-border rounded-2xl p-6 space-y-4">
        <h3 className="font-display font-bold text-lg">Widoczność sekcji na stronie</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="flex items-start justify-between gap-4 rounded-xl border border-border p-4 cursor-pointer">
            <span><span className="block text-sm font-semibold">Opinie klientów</span><span className="block text-xs text-muted-foreground mt-1">Sekcja z opiniami pod cennikiem (ręczne lub z Google).</span></span>
            <Switch data-testid="show-testimonials-switch" checked={content.show_testimonials !== false} onCheckedChange={(v) => setContent({ ...content, show_testimonials: v })} />
          </label>
          <label className="flex items-start justify-between gap-4 rounded-xl border border-border p-4 cursor-pointer">
            <span><span className="block text-sm font-semibold">Blog / artykuły (Wiedza)</span><span className="block text-xs text-muted-foreground mt-1">Kafelki artykułów w sekcji Wiedza, strona /wiedza, artykuły w czacie AI i mapie strony. Artykuły pozostają w panelu.</span></span>
            <Switch data-testid="show-blog-switch" checked={content.show_blog !== false} onCheckedChange={(v) => setContent({ ...content, show_blog: v })} />
          </label>
        </div>
      </div>

      <PopupEditor value={content.popup} onChange={(popup) => setContent({ ...content, popup })} />

      <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
        <h3 className="font-display font-bold text-lg">Dane kontaktowe</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Telefon</label>
            <Input data-testid="edit-contact-phone" value={content.contact_phone} onChange={setField("contact_phone")} />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Telefon komórkowy (przycisk „Zadzwoń”)</label>
            <Input data-testid="edit-contact-mobile" value={content.contact_mobile} onChange={setField("contact_mobile")} />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">WhatsApp (numer; puste = ukryty)</label>
            <Input data-testid="edit-contact-whatsapp" value={content.contact_whatsapp || ""} onChange={setField("contact_whatsapp")} placeholder="+48 698 841 266" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Messenger (nazwa strony FB lub link m.me; puste = ukryty)</label>
            <Input data-testid="edit-contact-messenger" value={content.contact_messenger || ""} onChange={setField("contact_messenger")} placeholder="np. twojebiuro lub facebook.com/twojebiuro" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">E-mail</label>
            <Input data-testid="edit-contact-email" value={content.contact_email} onChange={setField("contact_email")} />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Adres / lokalizacja</label>
            <Input data-testid="edit-contact-address" value={content.contact_address} onChange={setField("contact_address")} />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Godziny pracy</label>
            <Input data-testid="edit-contact-hours" value={content.contact_hours} onChange={setField("contact_hours")} />
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 space-y-6">
        <h3 className="font-display font-bold text-lg">Pakiety i ceny</h3>
        {content.packages.map((p, i) => (
          <div key={p.id} data-testid={`edit-package-${p.id}`} className="border border-border rounded-xl p-5 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Nazwa</label>
                <Input data-testid={`edit-package-name-${p.id}`} value={p.name} onChange={(e) => setPkg(i, "name", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Cena miesięczna</label>
                <Input data-testid={`edit-package-monthly-${p.id}`} value={p.price_monthly} onChange={(e) => setPkg(i, "price_monthly", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Cena 3 mc / mc</label>
                <Input data-testid={`edit-package-price3-${p.id}`} value={p.price_3 || ""} onChange={(e) => setPkg(i, "price_3", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Cena 6 mc / mc</label>
                <Input data-testid={`edit-package-price6-${p.id}`} value={p.price_6 || ""} onChange={(e) => setPkg(i, "price_6", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Cena 12 mc / mc</label>
                <Input data-testid={`edit-package-annual-${p.id}`} value={p.price_annual} onChange={(e) => setPkg(i, "price_annual", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Cena 24 mc / mc</label>
                <Input data-testid={`edit-package-price24-${p.id}`} value={p.price_24 || ""} onChange={(e) => setPkg(i, "price_24", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Odznaka</label>
                <Input data-testid={`edit-package-badge-${p.id}`} value={p.badge} onChange={(e) => setPkg(i, "badge", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Hasło pod nazwą (np. „Bez limitów!”)</label>
                <Input data-testid={`edit-package-tagline-${p.id}`} value={p.tagline || ""} onChange={(e) => setPkg(i, "tagline", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[["total_3", "3 mc łącznie"], ["total_6", "6 mc łącznie"], ["total_12", "12 mc łącznie"], ["total_24", "24 mc łącznie"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{label} (netto, do płatności)</label>
                  <Input data-testid={`edit-package-${k}-${p.id}`} value={p[k] || ""} onChange={(e) => setPkg(i, k, e.target.value)} />
                </div>
              ))}
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Notatka</label>
              <Input data-testid={`edit-package-note-${p.id}`} value={p.note} onChange={(e) => setPkg(i, "note", e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Cechy pakietu (jedna na linię; zacznij linię od <code className="font-mono">!</code>, aby wyróżnić cechę w cenniku)</label>
              <Textarea
                data-testid={`edit-package-features-${p.id}`}
                rows={4}
                value={p.features.join("\n")}
                onChange={(e) => setPkg(i, "features", e.target.value.split("\n").filter((f) => f.trim()))}
              />
            </div>
            <PackagePromoEditor pkg={p} onChange={(promo) => setPkg(i, "promo", promo)} />
          </div>
        ))}
      </div>

      <Billing24Editor content={content} onChange={setContent} />

      <ComparisonEditor content={content} onChange={setContent} />

      <PricingPromoEditor value={content.pricing_promo} onChange={(v) => setContent({ ...content, pricing_promo: v })} />

      <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Opinie klientów</h3>
          <button
            data-testid="testimonial-add-button"
            onClick={() => setContent({ ...content, testimonials: [...(content.testimonials || []), { name: "", role: "", text: "", rating: 5 }] })}
            className="text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted"
          >
            + Dodaj opinię
          </button>
        </div>
        {(content.testimonials || []).map((r, i) => (
          <div key={i} data-testid={`edit-testimonial-${i}`} className="border border-border rounded-xl p-5 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input data-testid={`edit-testimonial-name-${i}`} placeholder="Imię / nazwa" value={r.name} onChange={(e) => setTestimonial(i, "name", e.target.value)} />
              <Input data-testid={`edit-testimonial-role-${i}`} placeholder="Rola / firma" value={r.role || ""} onChange={(e) => setTestimonial(i, "role", e.target.value)} />
              <Input data-testid={`edit-testimonial-rating-${i}`} type="number" min={1} max={5} value={r.rating ?? 5} onChange={(e) => setTestimonial(i, "rating", Number(e.target.value) || 5)} />
            </div>
            <Textarea data-testid={`edit-testimonial-text-${i}`} rows={3} placeholder="Treść opinii" value={r.text} onChange={(e) => setTestimonial(i, "text", e.target.value)} />
            <button
              data-testid={`edit-testimonial-remove-${i}`}
              onClick={() => setContent({ ...content, testimonials: content.testimonials.filter((_, idx) => idx !== i) })}
              className="text-xs text-destructive hover:underline"
            >
              Usuń opinię
            </button>
          </div>
        ))}
      </div>

      <button
        data-testid="content-save-button"
        onClick={save}
        disabled={saving}
        className="bg-brand-teal hover:bg-brand-tealDark disabled:opacity-60 text-white font-semibold px-8 py-3 rounded-full text-sm transition-colors duration-200 inline-flex items-center gap-2"
      >
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
        Zapisz zmiany
      </button>
    </div>
  );
}

function Messages() {
  const [messages, setMessages] = useState(null);

  const load = () => api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => setMessages([]));
  useEffect(() => {
    load();
  }, []);

  if (!messages)
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-slate-600" />
      </div>
    );

  const markRead = async (id) => {
    await api.patch(`/admin/messages/${id}/read`);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/admin/messages/${id}`);
    toast.success("Wiadomość usunięta.");
    load();
  };

  if (messages.length === 0)
    return (
      <div data-testid="messages-empty" className="text-center py-16 text-muted-foreground text-sm">
        Brak wiadomości. Nowe zapytania z formularza pojawią się tutaj.
      </div>
    );

  return (
    <div data-testid="messages-list" className="space-y-4">
      {messages.map((m) => (
        <div
          key={m.id}
          data-testid={`message-${m.id}`}
          className={`bg-card border rounded-2xl p-5 ${m.read ? "border-border opacity-70" : "border-slate-500/40"}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-semibold text-sm flex items-center gap-2">
                {m.name}
                {!m.read && <span className="text-[10px] font-mono uppercase tracking-wider bg-brand-red text-white px-2 py-0.5 rounded-full">nowa</span>}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {m.email} {m.phone && `• ${m.phone}`} {m.service && `• ${m.service}`}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleString("pl-PL")}</span>
              {!m.read && (
                <button
                  data-testid={`message-read-${m.id}`}
                  onClick={() => markRead(m.id)}
                  aria-label="Oznacz jako przeczytane"
                  className="p-2 rounded-full hover:bg-muted transition-colors"
                >
                  <MailOpen size={15} />
                </button>
              )}
              <button
                data-testid={`message-delete-${m.id}`}
                onClick={() => remove(m.id)}
                aria-label="Usuń wiadomość"
                className="p-2 rounded-full hover:bg-destructive/10 text-destructive transition-colors"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-3 whitespace-pre-wrap">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

const ORDER_STATUS = {
  new: { label: "nowe", cls: "bg-brand-navy text-white" },
  in_progress: { label: "w trakcie", cls: "bg-brand-yellow text-brand-navy" },
  done: { label: "zakończone", cls: "bg-brand-teal text-white" },
};
const PAY_STATUS = {
  COMPLETED: { label: "opłacone", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
  PENDING: { label: "płatność w toku", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  WAITING_FOR_CONFIRMATION: { label: "płatność w toku", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  CANCELED: { label: "płatność anulowana", cls: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300" },
};
const BILLING_LABELS = { monthly: "miesięcznie", 3: "3 mc z góry", 6: "6 mc z góry", 12: "12 mc z góry" };
const LEGAL_FORMS = { jdg: "JDG", sc: "Sp. cywilna", spj: "Sp. jawna", spp: "Sp. partnerska", spk: "Sp.k.", spka: "S.K.A.", spzoo: "Sp. z o.o.", psa: "P.S.A.", sa: "S.A.", org: "Sp. w organizacji", spoldzielnia: "Spółdzielnia", fundacja: "Fundacja", stowarzyszenie: "Stowarzyszenie", oddzial: "Oddział przeds. zagr.", other: "Inna" };
const ENTITY_MODES = { existing: "Istniejący podmiot", new_jdg: "Rejestrowana JDG", new_krs_deed: "Rejestrowany podmiot KRS — akt założycielski (bez umowy/proformy)", new_krs_s24: "Rejestrowany podmiot KRS — S24 (proforma-kaucja)" };

function OrderRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-muted-foreground shrink-0 w-44">{label}</span>
      <span className="font-medium break-words">{value}</span>
    </div>
  );
}

const selectCls = "text-xs font-normal text-foreground bg-background border border-border rounded-lg px-3 py-2";

function Orders() {
  const [orders, setOrders] = useState(null);
  const [docs, setDocs] = useState([]);
  const [resending, setResending] = useState(null);
  const [filters, setFilters] = useState({ status: "", payment: "", date_from: "", date_to: "" });
  const isUnpaid = (o) => !["COMPLETED", "PENDING", "WAITING_FOR_CONFIRMATION", "CANCELED"].includes(o.payment_status);
  const matches = (o) => {
    if (filters.status && o.status !== filters.status) return false;
    if (filters.payment === "unpaid" && !isUnpaid(o)) return false;
    if (filters.payment === "pending" && !["PENDING", "WAITING_FOR_CONFIRMATION"].includes(o.payment_status)) return false;
    if (filters.payment === "completed" && o.payment_status !== "COMPLETED") return false;
    if (filters.payment === "canceled" && o.payment_status !== "CANCELED") return false;
    const day = (o.created_at || "").slice(0, 10);
    if (filters.date_from && day < filters.date_from) return false;
    if (filters.date_to && day > filters.date_to) return false;
    return true;
  };
  const filtered = (orders || []).filter(matches);
  const activeFilters = Object.values(filters).some(Boolean);

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  useEffect(() => {
    load();
    api.get("/admin/documents").then((r) => setDocs(r.data.documents.filter((d) => d.enabled && d.in_email))).catch(() => {});
  }, []);

  const resend = async (o) => {
    setResending(o.id);
    try {
      const r = await api.post(`/admin/orders/${o.id}/resend`);
      toast.success(`E-mail z umową wysłany ponownie na ${r.data.to}.`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(err.response?.status === 429 ? "Limit ponownych wysyłek dla tego zamówienia (5/h)." : typeof d === "string" ? d : "Nie udało się wysłać e-maila.");
    } finally {
      setResending(null);
    }
  };

  const resendVerify = async (o) => {
    setResending(o.id);
    try {
      const r = await api.post(`/orders/${o.id}/resend-verification`);
      toast.success(`Link potwierdzający wysłany ponownie na ${r.data.email}.`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(err.response?.status === 429 ? "Limit ponownych wysyłek dla tego zamówienia (3/h)." : typeof d === "string" ? d : "Nie udało się wysłać e-maila.");
    } finally {
      setResending(null);
    }
  };
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-slate-600" />
      </div>
    );

  const setStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}/status`, { status });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/admin/orders/${id}`);
    toast.success("Zamówienie usunięte.");
    load();
  };

  const proformaPdf = async (o) => {
    try {
      await downloadFile({ method: "get", url: `/admin/orders/${o.id}/proforma.pdf` }, `Proforma_${o.company}.pdf`);
    } catch {
      toast.error("Nie udało się wygenerować proformy PDF.");
    }
  };

  const contractPdf = async (o) => {
    try {
      await downloadFile({ method: "get", url: `/admin/orders/${o.id}/contract.pdf` }, `Umowa_${o.company}.pdf`);
    } catch {
      toast.error("Nie udało się wygenerować umowy PDF.");
    }
  };

  const exportCsv = async () => {
    try {
      await downloadFile({ method: "get", url: "/admin/orders/export.csv", params: filters }, "zamowienia.csv");
    } catch {
      toast.error("Nie udało się wyeksportować zamówień.");
    }
  };

  const refreshPayment = async (id) => {
    try {
      const r = await api.post(`/payments/admin/${id}/refresh`);
      toast.success(`Status płatności: ${PAY_STATUS[r.data.payment_status]?.label || r.data.payment_status}`);
      load();
    } catch {
      toast.error("Nie udało się sprawdzić statusu płatności.");
    }
  };

  if (orders.length === 0)
    return (
      <div data-testid="orders-empty" className="text-center py-16 text-muted-foreground text-sm">
        Brak zamówień. Zamówienia pakietów z cennika pojawią się tutaj.
      </div>
    );

  return (
    <div data-testid="orders-list" className="space-y-4">
      {(orders || []).some((o) => o.verify_status === "pending") && (
        <div data-testid="orders-pending-info" className="rounded-2xl border border-amber-400/50 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-xs leading-relaxed text-amber-800 dark:text-amber-200 flex gap-2.5">
          <Clock size={15} className="shrink-0 mt-0.5" />
          <span>Zamówienia ze statusem <strong>„oczekuje na potwierdzenie"</strong> czekają na kliknięcie przez klienta linku potwierdzającego adres e-mail. Jeśli klient nie potwierdzi adresu w ciągu <strong>24 godzin</strong>, takie zamówienie zostaje automatycznie usunięte (zasada zero przechowywania danych). Możesz wysłać link ponownie przyciskiem „Wyślij link ponownie".</span>
        </div>
      )}
      <div data-testid="orders-filters" className="bg-card border border-border rounded-2xl p-4 flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold text-muted-foreground flex flex-col gap-1">
          Status zamówienia
          <select data-testid="orders-filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={selectCls}>
            <option value="">wszystkie</option>
            {Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold text-muted-foreground flex flex-col gap-1">
          Płatność
          <select data-testid="orders-filter-payment" value={filters.payment} onChange={(e) => setFilters({ ...filters, payment: e.target.value })} className={selectCls}>
            <option value="">wszystkie</option>
            <option value="unpaid">nieopłacone</option>
            <option value="pending">w toku</option>
            <option value="completed">opłacone</option>
            <option value="canceled">anulowane</option>
          </select>
        </label>
        <label className="text-xs font-semibold text-muted-foreground flex flex-col gap-1">
          Od
          <input type="date" data-testid="orders-filter-from" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className={selectCls} />
        </label>
        <label className="text-xs font-semibold text-muted-foreground flex flex-col gap-1">
          Do
          <input type="date" data-testid="orders-filter-to" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className={selectCls} />
        </label>
        {activeFilters && (
          <button data-testid="orders-filter-clear" onClick={() => setFilters({ status: "", payment: "", date_from: "", date_to: "" })} className="text-xs font-semibold underline text-muted-foreground hover:text-foreground pb-2">
            wyczyść
          </button>
        )}
        <div className="ml-auto flex items-center gap-3">
          <span data-testid="orders-filter-count" className="text-xs text-muted-foreground">{filtered.length} z {orders.length}</span>
          <button
            data-testid="orders-export-csv-button"
            onClick={exportCsv}
            disabled={!filtered.length}
            className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted disabled:opacity-50 transition-colors"
          >
            <Download size={13} /> Eksport CSV ({filtered.length})
          </button>
        </div>
      </div>
      {!filtered.length && <div data-testid="orders-filter-empty" className="text-center py-10 text-muted-foreground text-sm">Brak zamówień spełniających filtry.</div>}
      {filtered.map((o) => {
        const st = ORDER_STATUS[o.status] || ORDER_STATUS.new;
        const pay = PAY_STATUS[o.payment_status];
        return (
          <div key={o.id} data-testid={`order-${o.id}`} className={`bg-card border rounded-2xl p-5 ${o.status === "done" ? "border-border opacity-70" : "border-slate-500/40"}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                  {o.company}
                  <span data-testid={`order-status-${o.id}`} className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                  {pay && (
                    <span data-testid={`order-payment-${o.id}`} className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${pay.cls}`}>
                      {pay.label}{o.payment_amount ? ` · ${(o.payment_amount / 100).toFixed(2).replace(".", ",")} zł` : ""}
                    </span>
                  )}
                  {pay && o.payment_status !== "COMPLETED" && o.payment_status !== "CANCELED" && (
                    <button data-testid={`order-payment-refresh-${o.id}`} onClick={() => refreshPayment(o.id)} className="text-[10px] underline text-muted-foreground hover:text-foreground">
                      sprawdź status
                    </button>
                  )}
                  {o.reminder_sent_at && (
                    <span data-testid={`order-reminder-${o.id}`} title={o.reminder_delivered ? "Przypomnienie o płatności wysłane" : "Próba wysyłki przypomnienia nie powiodła się (adres odrzucony)"} className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-muted-foreground">
                      przypomnienie {o.reminder_delivered ? "wysłane" : "nieudane"} {o.reminder_sent_at.slice(0, 10)}
                    </span>
                  )}
                  {o.verify_status && (
                    <span data-testid={`order-verify-status-${o.id}`} title="Potwierdzenie adresu e-mail przez klienta (double opt-in)" className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${o.verify_status === "verified" ? "bg-brand-teal/15 text-brand-teal" : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"}`}>
                      {o.verify_status === "verified" ? "✔ e-mail potwierdzony" : "⏳ oczekuje na potwierdzenie"}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Pakiet <strong>{o.package_name}</strong> • {BILLING_LABELS[o.billing] || o.billing}
                  {o.price && ` • ${fmtPrice(o.price)} zł netto/mc`}{o.total && ` (${fmtPrice(o.total)} zł netto z góry)`}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString("pl-PL")}</span>
                <select
                  data-testid={`order-status-select-${o.id}`}
                  value={o.status}
                  onChange={(e) => setStatus(o.id, e.target.value)}
                  className="text-xs border border-border rounded-full px-3 py-1.5 bg-background"
                >
                  {Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <button
                  data-testid={`order-contract-pdf-${o.id}`}
                  onClick={() => contractPdf(o)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-3 py-1.5 hover:bg-muted transition-colors"
                >
                  <FileDown size={13} /> Umowa PDF
                </button>
                <button
                  data-testid={`order-proforma-pdf-${o.id}`}
                  onClick={() => proformaPdf(o)}
                  title={o.proforma?.number || "Proforma zostanie wystawiona przy pobraniu"}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-3 py-1.5 hover:bg-muted transition-colors"
                >
                  <FileDown size={13} /> {o.proforma?.number ? `Proforma ${o.proforma.number}` : "Proforma PDF"}
                </button>
                {docs.map((d) => (
                  <a
                    key={d.id}
                    data-testid={`order-document-${d.id}-${o.id}`}
                    href={`/biuro-wirtualne-warszawa/dokumenty/${d.id}?order=${o.id}&t=${o.t}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-3 py-1.5 hover:bg-muted transition-colors"
                  >
                    <FileText size={13} /> {d.name}
                  </a>
                ))}
                <RegisteredAction order={o} onDone={load} />
                {o.verify_status === "pending" ? (
                  <button
                    data-testid={`order-resend-verify-${o.id}`}
                    onClick={() => resendVerify(o)}
                    disabled={resending === o.id}
                    title="Wyślij klientowi ponownie link potwierdzający adres e-mail"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-amber-400/60 text-amber-600 dark:text-amber-300 rounded-full px-3 py-1.5 hover:bg-amber-400/10 disabled:opacity-60 transition-colors"
                  >
                    {resending === o.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Wyślij link ponownie
                  </button>
                ) : (
                  <button
                    data-testid={`order-resend-${o.id}`}
                    onClick={() => resend(o)}
                    disabled={resending === o.id}
                    title="Wyślij klientowi ponownie e-mail z umową, dokumentami i linkiem do płatności"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-brand-teal/40 text-brand-teal rounded-full px-3 py-1.5 hover:bg-brand-teal/10 disabled:opacity-60 transition-colors"
                  >
                    {resending === o.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Wyślij umowę ponownie
                  </button>
                )}
                <button
                  data-testid={`order-delete-${o.id}`}
                  onClick={() => remove(o.id)}
                  aria-label="Usuń zamówienie"
                  className="p-2 rounded-full hover:bg-destructive/10 text-destructive transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1.5">
              <OrderRow label="Rodzaj zgłoszenia" value={ENTITY_MODES[o.entity_mode || "existing"]} />
              {o.source && <OrderRow label="Źródło" value={o.source.startsWith("article:") ? `Artykuł: ${o.source.slice(8)}` : ({ pricing: "Cennik", compare: "Tabela porównawcza", quiz: "Quiz", chat: "Czat", text: "Link w treści", link: "Link bezpośredni" }[o.source] || o.source)} />}
              <OrderRow label="Forma prawna" value={LEGAL_FORMS[o.legal_form] || o.legal_form} />
              <OrderRow label="NIP" value={o.nip} />
              {o.regon && <OrderRow label="REGON" value={o.regon} />}
              <OrderRow label="PESEL" value={o.pesel} />
              <OrderRow label="Podpisane dokumenty" value={o.docs_received_at ? `✔ otrzymano ${o.docs_received_at.slice(0, 10)}` : o.docs_reminder_sent_at ? `⏳ brak — przypomnienie wysłane ${o.docs_reminder_sent_at.slice(0, 10)}` : "⏳ jeszcze nie odesłano"} />
              {o.registry?.status && <OrderRow label="Weryfikacja NIP" value={o.registry.status === "verified" ? `✔ ${o.registry.name}${o.registry.vat_status ? ` (VAT: ${o.registry.vat_status})` : ""}` : o.registry.status === "not_found" ? "⚠ nie znaleziono w rejestrach GUS/MF" : o.registry.status === "unavailable" ? "rejestry niedostępne przy zamówieniu" : "—"} />}
              <OrderRow label="KRS" value={o.krs} />
              <OrderRow label="Reprezentant" value={o.representative} />
              <OrderRow label="E-mail" value={o.email} />
              <OrderRow label="Telefon" value={o.phone} />
              <OrderRow label="Adres siedziby" value={o.registered_address} />
              <OrderRow label="Adres korespondencyjny" value={o.mailing_address ? `${o.mailing_address}${o.mailing_country && o.mailing_country !== "PL" ? ", " + o.mailing_country : ""}` : ""} />
              <OrderRow label="Start umowy" value={o.start_date} />
              <OrderRow label="Pełnomocnicy do odbioru" value={(o.proxies || []).filter((p) => p.name).map((p) => (p.id_number ? `${p.name} (${p.id_number})` : p.name)).join("; ")} />
              {o.entity_mode === "new_krs_s24" && <OrderRow label="Akceptacja OWU" value={o.owu_consent ? "✔ tak" : "✖ nie"} />}
              <OrderRow label="Język klienta" value={o.lang?.toUpperCase()} />
              {Object.entries(o.extra || {}).map(([k, v]) => <OrderRow key={k} label={o.extra_labels?.[k] || k} value={v} />)}
            </div>
            <CompanyCheck nip={o.nip} />
            {o.notes && <p className="text-sm text-muted-foreground mt-3 whitespace-pre-wrap border-t border-border pt-3">{o.notes}</p>}
          </div>
        );
      })}
    </div>
  );
}

export default function Admin() {
  const { user, logout, refresh } = useAuth();

  useEffect(() => {
    const prev = document.title;
    document.title = "Panel administratora — Twoje Biuro";
    return () => {
      document.title = prev;
    };
  }, []);

  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 data-testid="admin-loading" className="animate-spin text-slate-600" size={28} />
      </div>
    );

  if (!user) return <LoginForm />;

  if (user.must_change_password)
    return (
      <div data-testid="admin-password-gate" className="min-h-screen flex flex-col items-center justify-center bg-background gap-6 px-4">
        <Logo className="h-9 text-slate-900 dark:text-white" showTagline={false} />
        <p className="text-sm text-muted-foreground text-center max-w-sm">Zalogowano jako {user.email}. Panel zostanie odblokowany po ustawieniu nowego hasła.</p>
        <ChangePasswordDialog forced onChanged={refresh} />
        <button data-testid="admin-gate-logout-button" onClick={logout} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors">
          <LogOut size={13} /> Wyloguj
        </button>
      </div>
    );

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Logo className="h-8 text-slate-900 dark:text-white" showTagline={false} />
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-home-link" className="text-xs text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5">
              <ArrowLeft size={13} /> Strona główna
            </Link>
            <span className="text-xs text-muted-foreground hidden sm:inline">{user.email}</span>
            <ChangePasswordDialog />
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors"
            >
              <LogOut size={13} /> Wyloguj
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <Tabs defaultValue="chat">
          <TabsList data-testid="admin-tabs" className="mb-8 flex-wrap h-auto">
            <TabsTrigger data-testid="tab-stats" value="stats">Statystyki</TabsTrigger>
            <TabsTrigger data-testid="tab-chat" value="chat">Czat</TabsTrigger>
            <TabsTrigger data-testid="tab-orders" value="orders">Zamówienia</TabsTrigger>
            <TabsTrigger data-testid="tab-generator" value="generator">Umowa + proforma</TabsTrigger>
            <TabsTrigger data-testid="tab-uploads" value="uploads">Przesłane dokumenty</TabsTrigger>
            <TabsTrigger data-testid="tab-content" value="content">Treść strony</TabsTrigger>
            <TabsTrigger data-testid="tab-texts" value="texts">Teksty strony</TabsTrigger>
            <TabsTrigger data-testid="tab-contract" value="contract">Umowa PDF</TabsTrigger>
            <TabsTrigger data-testid="tab-documents" value="documents">Generator dokumentów</TabsTrigger>
            <TabsTrigger data-testid="tab-faq" value="faq">FAQ</TabsTrigger>
            <TabsTrigger data-testid="tab-articles" value="articles">Artykuły</TabsTrigger>
            <TabsTrigger data-testid="tab-legalization" value="legalization">Legalizacja</TabsTrigger>
            <TabsTrigger data-testid="tab-owu" value="owu">OWU</TabsTrigger>
            <TabsTrigger data-testid="tab-emails" value="emails">E-maile</TabsTrigger>
            <TabsTrigger data-testid="tab-smtp" value="smtp">Konto pocztowe</TabsTrigger>
            <TabsTrigger data-testid="tab-integrations" value="integrations">Integracje / Klucze API</TabsTrigger>
            <TabsTrigger data-testid="tab-proformas" value="proformas">Rejestr proform</TabsTrigger>
            <TabsTrigger data-testid="tab-staff" value="staff">Pracownicy</TabsTrigger>
            <TabsTrigger data-testid="tab-security" value="security">Bezpieczeństwo</TabsTrigger>
            <TabsTrigger data-testid="tab-archive" value="archive">Archiwum</TabsTrigger>
          </TabsList>
          <TabsContent value="archive">
            <EmailArchiveAdmin />
          </TabsContent>
          <TabsContent value="security">
            <SecurityAdmin />
          </TabsContent>
          <TabsContent value="staff">
            <StaffAdmin />
          </TabsContent>
          <TabsContent value="articles">
            <ArticlesAdmin />
          </TabsContent>
          <TabsContent value="legalization">
            <PageAdmin slug="legalization" title="Strona „Legalizacja dokumentów”" path="/biuro-wirtualne-warszawa/legalizacja-dokumentow" hint="link w menu i kafelek w Ofercie" />
          </TabsContent>
          <TabsContent value="quotes">
            <QuotesAdmin />
          </TabsContent>
          <TabsContent value="owu">
            <PageAdmin slug="owu" title="Ogólne Warunki Świadczenia Usług (OWU)" path="/biuro-wirtualne-warszawa/owu" hint="link w stopce i w formularzu zamówienia S24; sekcje wyświetlane jedna pod drugą" />
          </TabsContent>
          <TabsContent value="emails">
            <EmailTemplatesAdmin />
          </TabsContent>
          <TabsContent value="smtp">
            <SmtpAdmin />
          </TabsContent>
          <TabsContent value="integrations">
            <IntegrationsAdmin />
          </TabsContent>
          <TabsContent value="proformas">
            <ProformaRegistryAdmin />
          </TabsContent>
          <TabsContent value="stats">
            <StatsAdmin />
          </TabsContent>
          <TabsContent value="chat">
            <div data-testid="admin-chat-operator-cta" className="bg-card border border-border rounded-2xl p-8 text-center">
              <h3 className="font-display font-bold text-lg">Obsługa czatu w konsoli operatora</h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-xl mx-auto leading-relaxed">
                Czat na stronie jest aktywny tylko wtedy, gdy operator jest zalogowany i dostępny w konsoli. Gdy nikt nie jest dostępny, klienci zostawiają numer telefonu (oddzwaniamy). Prośby o oddzwonienie przychodzą na skrzynkę biura.
              </p>
              <a data-testid="admin-open-operator-link" href="/operator" target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 bg-brand-teal text-white hover:bg-brand-tealDark font-semibold px-6 py-3 rounded-full text-sm transition-colors">
                Otwórz konsolę operatora
              </a>
            </div>
          </TabsContent>
          <TabsContent value="messages">
            <Messages />
          </TabsContent>
          <TabsContent value="orders">
            <Orders />
          </TabsContent>
          <TabsContent value="generator">
            <div className="space-y-6">
              <OrderGenerator context="admin" />
              <ProformaGenerator context="admin" />
            </div>
          </TabsContent>
          <TabsContent value="uploads">
            <UploadsAdmin />
          </TabsContent>
          <TabsContent value="content">
            <div className="mb-8"><GoogleReviewsAdmin /></div>
            <ContentEditor />
          </TabsContent>
          <TabsContent value="texts">
            <TextsAdmin />
          </TabsContent>
          <TabsContent value="contract">
            <ContractEditor />
          </TabsContent>
          <TabsContent value="documents">
            <DocumentsAdmin />
          </TabsContent>
          <TabsContent value="faq">
            <FaqAdmin endpoint="/admin/faq" heading="FAQ na stronie głównej" description="Pytania i odpowiedzi w sekcji FAQ na stronie głównej (link „FAQ” w menu). Asystent AI w czacie również korzysta z tych odpowiedzi. Wersja angielska jest opcjonalna — gdy pusta, pokazujemy polską." max={20} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
